import { useAppSiteMargin } from "@/Shared/Hooks/useAppSiteMargin";
import { Page, PageContainer } from "@/App/Page";
import ScrollToTop from "@/Shared/Layout/ScrollToTop";
import AcceptInvitationScreen from "@/Features/Authentication/AcceptInvitationScreen";
import { CreateAccountScreen } from "@/Features/Authentication/CreateAccountScreen";
import { QuickLoginScreen } from "@/Features/Authentication/QuickLoginScreen";
import RequestResetPasswordScreen from "@/Features/Authentication/RequestResetPasswordScreen";
import ResetPasswordScreen from "@/Features/Authentication/ResetPasswordScreen";
import SignInScreen from "@/Features/Authentication/SignInScreen";
import { useAuthentication } from "@/Slices/authenticationSlice";
import BookShipmentRouter from "@/Routers/BookShipmentRouter";
import ExternalAddCreditCardScreen from "@/Features/CreditCards/ExternalAddCreditCardScreen";
import DashboardRouter from "@/Routers/DashboardRouter";
import { DashboardFooterLayout } from "@/App/DashboardFooterLayout";
import { AppFadeInShell } from "@/App/AppFadeInShell";
import { AppSiteMargin } from "@/App/AppSiteMargin";
import NavBar from "@/Features/Nav/Bars/NavBar";
import { ReferralScreen } from "@/Features/Referral/ReferralScreen";
import ResetScreen from "@/Features/Reset/ResetScreen";
import ConnectBigCommerceScreen from "@/Features/BigCommerce/ConnectBigCommerceScreen";
import ConnectShopifyScreen from "@/Features/Shopify/ConnectShopifyScreen";
import { LookUpTrackingScreen } from "@/Features/Tracking/LookUpTrackingScreen";
import TrackShipmentScreen from "@/Features/Tracking/TrackShipmentScreen";
import UploadPhotosScreen from "@/Features/UploadPhotos/UploadPhotosScreen";
import { useSessionExpiry } from "@/Hooks/useSessionExpiry";
import type React from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import { IntercomProvider } from "react-use-intercom";
import NotAuthedRoute from "@/Routing/NotAuthedRoute";
import type { KnownRoute, RouteDefinition } from "@/Routing/KnownRoute";
import { RouteFallback } from "@/Routing/RouteFallback";
import { signedInRoutes } from "@/Routers/signedInRoutes";
import { isDemo, isProduction } from "./Environment";
import { TestErrorScreen } from "@/Features/ErrorHandling/TestErrorScreen";
import { ApiDocumentationScreen } from "@/Features/ApiAccess/ApiDocumentationScreen";
import ConnectivityTestScreen from "@/Features/ConnectivityTest/ConnectivityTestScreen";

const bookRoute: KnownRoute = { path: "/book/", exact: false };

// Only for signed-out visitors: NotAuthedRoute sends a signed-in one home.
const signedOutRoutes: Array<RouteDefinition> = [
  { path: "/sign-in", element: <SignInScreen /> },
  { path: "/referral", element: <ReferralScreen /> },
  { path: "/create-account", element: <CreateAccountScreen /> },
  { path: "/reset-password", element: <ResetPasswordScreen /> },
  { path: "/request-reset-password", element: <RequestResetPasswordScreen /> },
  { path: "/qli", element: <QuickLoginScreen /> },
  { path: "/accept-invitation", element: <AcceptInvitationScreen /> },
  { path: "/track", element: <LookUpTrackingScreen /> },
];

// Render whether or not the visitor is signed in.
const publicRoutes: Array<RouteDefinition> = [
  { path: "/test-error", element: <TestErrorScreen /> },
  // See note below
  { path: "/track-shipment", element: <TrackShipmentScreen /> },
  // CS Sept 2023. I suspect some email relays/clients are rewriting urls that mention tracking and obfuscate any mention of a trackingId. So we're adding a new endpoint that doesn't mention track/trackingId to work around this
  { path: "/t", element: <TrackShipmentScreen /> },
  { path: "/reset", element: <ResetScreen /> },
  { path: "/connectivity-test", element: <ConnectivityTestScreen /> },
  // Popup target for the Shopify embedded app's "Connect" button. Lives outside the
  // isAuthenticated gate because the route needs to render whether or not the user is
  // signed in — the component itself prompts for sign-in when needed.
  { path: "/connect-shopify", element: <ConnectShopifyScreen /> },
  // Popup target for the BigCommerce embedded app's "Connect" button. Outside the
  // isAuthenticated gate for the same reason as Shopify's: the route has to render whether
  // or not the user is signed in, because the component prompts for sign-in itself.
  { path: "/connect-bigcommerce", element: <ConnectBigCommerceScreen /> },
];

// Public too, but rendered outside the site margin: we don't want the container CSS to apply.
const standaloneRoutes: Array<RouteDefinition> = [
  { path: "/upload-photos", element: <UploadPhotosScreen /> },
  { path: "/api-docs", element: <ApiDocumentationScreen /> },
];

const openRoutes: Array<KnownRoute> = [
  bookRoute,
  ...[...signedOutRoutes, ...publicRoutes, ...standaloneRoutes].map(
    ({ path }) => ({ path, exact: true }),
  ),
];

const NavBarredAppRouter_: React.FC = () => {
  const { isAuthenticated, user } = useAuthentication();
  const siteMargin = useAppSiteMargin();

  const location = useLocation();

  function showNavBar() {
    if (location.pathname === "/sign-in") {
      return false;
    }

    if (location.pathname === "/api-docs") {
      return false;
    }

    if (location.pathname === "/create-account") {
      return false;
    }

    // The password-reset screens are full standalone branded screens (own logo
    // + photo split, like sign-in), so the top nav would just duplicate the logo.
    if (location.pathname === "/request-reset-password") {
      return false;
    }

    if (location.pathname === "/reset-password") {
      return false;
    }

    if (location.pathname === "/accept-invitation") {
      return false;
    }

    if (location.pathname === "/upload-photos") {
      return false;
    }

    // The connect popups are branded, standalone cards — no dashboard chrome.
    if (
      location.pathname === "/connect-shopify" ||
      location.pathname === "/connect-bigcommerce"
    ) {
      return false;
    }

    return true;
  }

  return (
    <>
      {showNavBar() && <NavBar />}
      <AppSiteMargin paddingWidth={siteMargin}>
        <PageContainer>
          {isAuthenticated && <DashboardRouter />}

          <Page path={bookRoute.path}>{() => <BookShipmentRouter />}</Page>

          {signedOutRoutes.map(({ path, element }) => (
            <NotAuthedRoute key={path} exact path={path}>
              {element}
            </NotAuthedRoute>
          ))}

          {publicRoutes.map(({ path, element }) => (
            <Page key={path} exact path={path}>
              {() => element}
            </Page>
          ))}
        </PageContainer>
      </AppSiteMargin>
      {standaloneRoutes.map(({ path, element }) => (
        <Page key={path} exact path={path}>
          {() => element}
        </Page>
      ))}

      <RouteFallback
        signedInRoutes={signedInRoutes(user?.email).map(({ path }) => ({
          path,
          exact: true,
        }))}
        openRoutes={openRoutes}
      />
    </>
  );
};

const AppRouter: React.FC = () => {
  useSessionExpiry();

  // In production / demo use the real intercom (as they're both customer accessible)
  const INTERCOM_APP_ID = isProduction() || isDemo() ? "oj0p8brq" : "w7bho49n";

  return (
    <IntercomProvider appId={INTERCOM_APP_ID}>
      <AppFadeInShell>
        <DashboardFooterLayout>
          <ScrollToTop />
          <Routes>
            <Route
              path="/add-credit-card"
              element={<ExternalAddCreditCardScreen />}
            />
            <Route path="*" element={<NavBarredAppRouter_ />} />
          </Routes>
        </DashboardFooterLayout>
      </AppFadeInShell>
    </IntercomProvider>
  );
};

export default AppRouter;
